import { DesktopTutorial } from './DesktopTutorial'
import { useEffect } from 'react'
import { Link, Outlet } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query'
import { Building2, ChevronsUpDown } from 'lucide-react'
import { Sidebar } from './Sidebar'
import { WorkspaceErrorBoundary } from './WorkspaceErrorBoundary'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { organizationsApi } from '@/api/organizationsApi'
import { assistantsApi } from '@/api/assistantsApi'
import apiClient from '@/api/client'
import type { User } from '@/types'
import { Avatar, Button } from '@/components/ui'
import { toast } from '@/components/ui/Toast'

export function AppLayout() {
  const location = useLocation()
  const { currentAssistantId, setCurrentAssistant, setUser } = useAuthStore()
  const client = useQueryClient()
  const mutating = useIsMutating() > 0
  const me = useQuery({ queryKey: ['desktop-me'], queryFn: async () => (await apiClient.get<User>('/users/me')).data })
  const clubs = useQuery({ queryKey: ['desktop-clubs'], queryFn: organizationsApi.getMyOrganizations })
  const clubId = me.isSuccess ? clubs.data?.find(c => c.id === me.data.selected_organization_id)?.id ?? clubs.data?.[0]?.id : undefined
  const directions = useQuery({ queryKey: ['desktop-directions', clubId], queryFn: () => assistantsApi.getByOrganizationId(clubId!), enabled: !!clubId })
  const directionIsValid = directions.data?.some(d => d.id === currentAssistantId)
  useEffect(() => { if (me.data) setUser(me.data) }, [me.data, setUser])
  useEffect(() => {
    if (me.isSuccess && clubs.isSuccess && !clubs.data.length) setCurrentAssistant(null)
  }, [me.isSuccess, clubs.isSuccess, clubs.data, setCurrentAssistant])
  useEffect(() => {
    if (directions.isSuccess && !directionIsValid) setCurrentAssistant(directions.data[0]?.id ?? null)
  }, [directions.isSuccess, directions.data, directionIsValid, setCurrentAssistant])
  const switchClub = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.put(`/users/${me.data!.id}/selected-organization`, { selected_organization_id: id })
      return id
    },
    onSuccess: id => {
      setCurrentAssistant(null)
      client.setQueryData(['desktop-me'], { ...me.data!, selected_organization_id: id })
    },
    onError: () => toast.error('Не удалось переключить клуб. Попробуйте ещё раз'),
  })
  const canSwitch = () => window.dispatchEvent(new Event('procoach:before-scope-change', { cancelable: true }))
  const error = me.isError || clubs.isError || directions.isError
  const ready = me.isSuccess && clubs.isSuccess && ((!clubId && !currentAssistantId) || (directions.isSuccess && ((!directions.data.length && !currentAssistantId) || directionIsValid)))
  return <div className="min-h-screen">
    <a href="#workspace-content" className="pc-skip">Перейти к содержимому</a>
    <Sidebar />
    <main className="pc-main" id="workspace-content" tabIndex={-1}>
      <div className="pc-workspace-bar">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <Building2 size={18} className="shrink-0" style={{ color: 'var(--text-hint)' }} />
          <label className="sr-only" htmlFor="workspace-club">Клуб</label>
          <select id="workspace-club" className="pc-select text-[13px] max-w-[230px]" aria-label="Клуб" value={clubId ?? ''} disabled={mutating || !clubs.data?.length || !me.data} onChange={e => { if (canSwitch()) switchClub.mutate(e.target.value) }}>
            {!clubs.data?.length && <option value="">Нет клубов</option>}
            {clubs.data?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <ChevronsUpDown size={14} style={{ color: 'var(--border)' }} />
          <select className="pc-select text-[13px] max-w-[240px]" aria-label="Направление" value={directionIsValid ? currentAssistantId! : ''} disabled={mutating || !directions.data?.length} onChange={e => { if (canSwitch()) setCurrentAssistant(e.target.value) }}>
            {!directions.data?.length && <option value="">Нет направлений</option>}
            {directions.data?.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <Link to="/profile" className="flex items-center gap-2.5 shrink-0" aria-label="Открыть профиль">
          <span className="text-[13px] font-medium hidden xl:inline">{me.data?.name}</span><Avatar name={me.data?.name || 'Тренер'} size={32} />
        </Link>
      </div>
      {ready && me.data && clubId && <DesktopTutorial key={`${me.data.id}:${clubId}`} userId={me.data.id} clubId={clubId} direction={!!directionIsValid} offer={directions.isSuccess && directions.data.length === 0} />}
      {error ? <div className="pc-page"><div role="alert" className="pc-error">Не удалось загрузить рабочее пространство. <Button variant="ghost" onClick={() => { void me.refetch(); void clubs.refetch(); if (clubId) void directions.refetch() }}>Повторить</Button></div></div>
        : ready ? <div key={`${clubId ?? 'none'}:${currentAssistantId ?? 'none'}`}><WorkspaceErrorBoundary key={location.pathname}><Outlet /></WorkspaceErrorBoundary></div>
          : <div className="pc-page pc-muted" role="status">Загружаем рабочее пространство...</div>}
    </main>
  </div>
}
